import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, DataTable, Button, Badge, ConfirmDialog, Toggle, Modal, StarRating, ImageUpload } from '../../components/common';
import { IMAGE_SIZE_PRESETS } from '../../utils/imageCompressor';
import ReviewFormInput from './ReviewFormInput';
import { reviewService } from '../../services/reviewService';
import { truncate } from '../../utils/stringUtils';
import { formatDate } from '../../utils/dateUtils';
import { useDebounce, useModal, useForm } from '../../hooks';
import { validators } from '../../utils/validators';
import './ReviewsPage.css';

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  const formModal = useModal();
  const deleteModal = useModal();
  const [saving, setSaving] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const debouncedSearch = useDebounce(search, 300);
  const pageSize = 10;

  const validationRules = {
    clientName: [validators.required, validators.minLength(2)],
    reviewText: [validators.required, validators.minLength(10)],
  };

  const form = useForm({
    clientName: '',
    clientPhoto: null,
    rating: 5,
    reviewText: '',
    isApproved: false
  }, validationRules);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await reviewService.getAll({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch
      });
      setReviews(response.data.reviews);
      setTotalItems(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleOpenForm = (review = null) => {
    if (review) {
      form.setValues({
        id: review.id,
        clientName: review.clientName,
        clientPhoto: review.clientPhoto || null,
        rating: review.rating || 5,
        reviewText: review.reviewText || '',
        isApproved: review.isApproved
      });
    } else {
      form.reset();
    }
    formModal.open(review);
  };

  const demoReviews = [
    { clientName: 'Rahul Sharma',  rating: 5, reviewText: 'Excellent structural design services. The team delivered beyond our expectations and finished well ahead of schedule.' },
    { clientName: 'Priya Mehta',  rating: 4, reviewText: 'Very professional team. They understood our requirements perfectly and delivered a beautiful design for our office building.' },
    { clientName: 'Amit Patel',   rating: 5, reviewText: 'Outstanding infrastructure planning services. The attention to detail and commitment to quality is remarkable.' },
    { clientName: 'Sunita Verma', rating: 4, reviewText: 'Great experience working with Design & Infra. They made the entire process smooth and hassle-free from start to finish.' },
  ];

  const handleFillDemo = () => {
    const sample = demoReviews[Math.floor(Math.random() * demoReviews.length)];
    form.setValues({
      clientName: sample.clientName,
      rating:     sample.rating,
      reviewText: sample.reviewText,
      isApproved: false,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.validate()) {
      toast.error('Please fix the form errors');
      return;
    }
    
    setSaving(true);
    try {
      // Only send fields the API accepts
      const photo = form.values.clientPhoto;
      const formData = {
        clientName:  form.values.clientName,
        clientPhoto: photo?.preview ?? photo ?? null,
        rating:      form.values.rating,
        reviewText:  form.values.reviewText,
        isApproved:  form.values.isApproved,
      };

      if (formModal.data) {
        await reviewService.update(formModal.data.id, formData);
        toast.success('Review updated successfully');
      } else {
        await reviewService.create(formData);
        toast.success('Review added successfully');
      }
      
      formModal.close();
      fetchReviews();
    } catch (error) {
      toast.error('Failed to save review');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleApproval = async (id) => {
    try {
      const response = await reviewService.toggleApproval(id);
      const updated = response.data;
      setReviews(prev => prev.map(item =>
        item.id === id ? { ...item, isApproved: updated.isApproved } : item
      ));
      toast.success('Approval status updated');
    } catch (error) {
      toast.error('Failed to update approval status');
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await reviewService.delete(deleteModal.data);
      toast.success('Review deleted successfully');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to delete review');
    } finally {
      setDeleteLoading(false);
      deleteModal.close();
    }
  };

  const columns = [
    {
      header: 'Client',
      render: (row) => (
        <div className="review-client">
          <div className="client-avatar">
            {row.clientPhoto ? (
              <img src={row.clientPhoto} alt={row.clientName} />
            ) : (
              <span>{row.clientName.charAt(0)}</span>
            )}
          </div>
          <div className="client-info">
            <span className="client-name">{row.clientName}</span>
            <span className="review-date">{formatDate(row.createdAt)}</span>
          </div>
        </div>
      )
    },
    {
      header: 'Rating',
      width: '120px',
      render: (row) => (
        <StarRating value={row.rating} readonly size="small" />
      )
    },
    {
      header: 'Review',
      render: (row) => (
        <span className="review-text">{truncate(row.reviewText, 80)}</span>
      )
    },
    {
      header: 'Approved',
      width: '100px',
      render: (row) => (
        <Toggle
          name={`approval-${row.id}`}
          checked={row.isApproved}
          onChange={() => handleToggleApproval(row.id)}
          size="small"
        />
      )
    },
    {
      header: 'Status',
      width: '100px',
      render: (row) => (
        <Badge variant={row.isApproved ? 'success' : 'warning'}>
          {row.isApproved ? 'Approved' : 'Pending'}
        </Badge>
      )
    },
    {
      header: 'Actions',
      width: '100px',
      align: 'center',
      render: (row) => (
        <div className="action-buttons">
          <button 
            className="action-btn edit"
            title="Edit"
            onClick={() => handleOpenForm(row)}
          >
            <FiEdit2 />
          </button>
          <button 
            className="action-btn delete"
            title="Delete"
            onClick={() => deleteModal.open(row.id)}
          >
            <FiTrash2 />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="reviews-page">
      <Card
        title="Reviews"
        subtitle={`${totalItems} reviews`}
        action={
          <Button icon={<FiPlus />} onClick={() => handleOpenForm()}>
            Add Review
          </Button>
        }
      >
        <DataTable
          columns={columns}
          data={reviews}
          loading={loading}
          searchable
          searchPlaceholder="Search reviews..."
          searchValue={search}
          onSearch={handleSearch}
          pagination
          pageSize={pageSize}
          totalItems={totalItems}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          exportable
          exportFilename="reviews-export"
          emptyMessage="No reviews found"
        />
      </Card>

      {/* Form Modal */}
      <Modal
        isOpen={formModal.isOpen}
        onClose={formModal.close}
        title={formModal.data ? 'Edit Review' : 'Add Review'}
        size="medium"
        footer={
          <>
            <Button variant="ghost" onClick={formModal.close}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={saving}>
              {formModal.data ? 'Update' : 'Save'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="review-form">
          {!formModal.data && (
            <div className="demo-data-bar">
              <button type="button" className="demo-data-btn" onClick={handleFillDemo}>
                ✦ Fill Demo Data
              </button>
            </div>
          )}
          <ReviewFormInput
            name="clientName"
            label="Client Name"
            placeholder="Enter client name"
            value={form.values.clientName}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            error={form.errors.clientName}
            touched={form.touched.clientName}
            required
          />
          <ImageUpload
            label="Client Photo (optional)"
            value={form.values.clientPhoto}
            onChange={(photo) => form.setFieldValue('clientPhoto', photo)}
            maxSize={2}
            {...IMAGE_SIZE_PRESETS.small}
            accept="image/jpeg,image/png,image/webp"
          />
          <div className="rating-field">
            <label>Rating</label>
            <StarRating
              value={form.values.rating}
              onChange={(rating) => form.setFieldValue('rating', rating)}
              size="large"
            />
          </div>
          <div className="textarea-wrapper">
            <label>Review Text <span className="required">*</span></label>
            <textarea
              name="reviewText"
              placeholder="Enter the review text..."
              value={form.values.reviewText}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              rows={4}
            />
            {form.errors.reviewText && form.touched.reviewText && (
              <span className="input-error">{form.errors.reviewText}</span>
            )}
          </div>
          <Toggle
            name="isApproved"
            checked={form.values.isApproved}
            onChange={(e) => form.setFieldValue('isApproved', e.target.checked)}
            label="Approved"
          />
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDelete}
        title="Delete Review"
        message="Are you sure you want to delete this review? This action cannot be undone."
        confirmText="Delete"
        loading={deleteLoading}
      />
    </div>
  );
};

export default ReviewsPage;
